import { useState, useCallback, useEffect } from 'react';
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { CompetitorComparison } from '@/components/CompetitorComparison';
import { CompetitorGrowthChart } from '@/components/CompetitorGrowthChart';
import { NextScrapingProgress } from '@/components/NextScrapingProgress';
import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import LoadingRadar from '@/components/ui/loading-radar';
import { Button } from '@/components/ui/button';
import { TabButton } from '@/components/ui/tab-button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Target, Users, Trash2, BarChart, TrendingUp, Loader2, Instagram } from 'lucide-react';
import { useCompetitors } from '@/hooks/useCompetitors';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import { extractInstagramUsername, validateInstagramInput } from '@/utils/instagramUsername';
import { useProfileVerification, ProfileAvatar, VerifiableProfileInput } from '@/components/onboarding/ProfileVerification';
import { useUserId } from '@/hooks/useUserId';
import { useTierLimits } from '@/hooks/useTierLimits';
import { CompetitorsSkeleton } from '@/components/skeletons/CompetitorsSkeleton';

type TimeRange = 'week' | 'month';

const Competitors = () => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const userId = useUserId();
  const [competitorToDelete, setCompetitorToDelete] = useState<{ id: string; username: string } | null>(null);
  const [activeTab, setActiveTab] = useState('growth');
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [isInitialized, setIsInitialized] = useState(false);
  const tierLimits = useTierLimits();
  const maxCompetitors = tierLimits.limits.maxCompetitors;
  const { competitors, isLoading, addCompetitor, removeCompetitor } = useCompetitors(maxCompetitors);

  const [addInputs, setAddInputs] = useState<string[]>(['']);
  const [validationErrors, setValidationErrors] = useState<Record<number, string>>({});
  const [isAdding, setIsAdding] = useState(false);
  const { verifications, verifyProfile, clearVerification, removeAndReindex } = useProfileVerification(userId);

  const freeSlots = maxCompetitors - competitors.length;
  const slotLimitReached = freeSlots <= 0;
  const monthlyLimitReached = tierLimits.remainingCompetitorAdds <= 0;
  const isLimitReached = slotLimitReached || monthlyLimitReached;
  const filledInputs = addInputs.filter(v => v.trim() !== '').length;
  const totalCount = competitors.length + filledInputs;
  const canAddMoreInputs = !isLimitReached && addInputs.length < freeSlots;

  // Initialize page after loading completes
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => {
        setIsInitialized(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // Trim inputs when saved competitors change and exceed limit
  useEffect(() => {
    if (isLimitReached) {
      setAddInputs(['']);
    } else if (addInputs.length > freeSlots) {
      setAddInputs(prev => prev.slice(0, freeSlots));
    }
  }, [competitors.length]);

  const handleInputChange = useCallback((index: number, value: string) => {
    const lowercaseValue = value.toLowerCase();
    setAddInputs(prev => { const next = [...prev]; next[index] = lowercaseValue; return next; });
    clearVerification(index);
    const error = validateInstagramInput(lowercaseValue);
    setValidationErrors(prev => {
      const next = { ...prev };
      if (error) next[index] = error; else delete next[index];
      return next;
    });
  }, [clearVerification]);

  const handleRemoveInput = useCallback((index: number) => {
    setAddInputs(prev => prev.filter((_, i) => i !== index));
    removeAndReindex(index);
    setValidationErrors(prev => {
      const next: Record<number, string> = {};
      for (const [key, val] of Object.entries(prev)) {
        const idx = Number(key);
        if (idx < index) next[idx] = val;
        else if (idx > index) next[idx - 1] = val;
      }
      return next;
    });
  }, [removeAndReindex]);

  const handleAddInput = useCallback(() => {
    if (canAddMoreInputs) setAddInputs(prev => [...prev, '']);
  }, [canAddMoreInputs]);


  const handleAddVerifiedCompetitors = async () => {
    const verifiedUsernames = addInputs
      .map((val, idx) => {
        const v = verifications[idx];
        if (v?.status === 'success' && !v.is_private && !v.is_blocked) return extractInstagramUsername(val);
        return null;
      })
      .filter(Boolean) as string[];
    if (verifiedUsernames.length === 0) return;
    setIsAdding(true);
    try {
      let addedCount = 0;
      for (const username of verifiedUsernames) {
        const success = await addCompetitor(username);
        if (success) addedCount++;
        else break; // stop on first failure (likely a limit hit)
      }
      if (addedCount > 0) {
        setAddInputs(['']);
        for (const key of Object.keys(verifications)) clearVerification(Number(key));
        await tierLimits.refresh();
        toast({ title: 'Konkurenci dodani', description: `Dodano ${addedCount} ${addedCount === 1 ? 'profil' : 'profile'}` });
      }
    } catch (err) {
      toast({ title: 'Błąd', description: err instanceof Error ? err.message : 'Nie udało się dodać', variant: 'destructive' });
    } finally {
      setIsAdding(false);
    }
  };

  const verifiedCount = addInputs.filter((_, i) => {
    const v = verifications[i];
    return v?.status === 'success' && !v.is_private && !v.is_blocked;
  }).length;

  const formatNumber = (num: number | undefined) => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const handleRemoveCompetitor = async () => {
    if (competitorToDelete) {
      await removeCompetitor(competitorToDelete.id);
      setCompetitorToDelete(null);
    }
  };

  const renderVerificationInputs = () => {
    if (isLimitReached) {
      return (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            {monthlyLimitReached && !slotLimitReached
              ? `Wykorzystano ${tierLimits.changeCounts.competitorAdds}/${tierLimits.limits.monthlyCompetitorAdds} dodań w tym okresie. Odnowi się automatycznie.`
              : `Osiągnięto limit ${maxCompetitors} konkurentów. Usuń jednego, aby dodać nowego.`}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {addInputs.map((value, index) => (
          <VerifiableProfileInput
            key={index}
            index={index}
            value={value}
            onChange={(idx, val) => handleInputChange(idx, val)}
            onRemove={handleRemoveInput}
            onVerify={verifyProfile}
            onEnterAdd={handleAddInput}
            verification={verifications[index]}
            validationError={validationErrors[index]}
            canRemove={addInputs.length > 1}
            icon={<Instagram className="h-4 w-4" />}
            totalCount={addInputs.length}
          />
        ))}

        <div className="flex items-center gap-2 pt-1">
          {canAddMoreInputs && (
            <Button variant="outline" size="sm" onClick={handleAddInput} className="text-xs">
              <Plus className="mr-1 h-3 w-3" /> Dodaj kolejny
            </Button>
          )}
          {verifiedCount > 0 && (
            <Button onClick={handleAddVerifiedCompetitors} disabled={isAdding} size="sm">
              {isAdding ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Dodawanie...</>) : (
                <>Dodaj {verifiedCount} {verifiedCount === 1 ? 'profil' : verifiedCount < 5 ? 'profile' : 'profili'}</>
              )}
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{totalCount} z {maxCompetitors} konkurentów · {tierLimits.remainingCompetitorAdds} dodań pozostało w tym miesiącu</p>
      </div>
    );
  };

  if (!isInitialized) {
    return <CompetitorsSkeleton />;
  }

  return (
    <>
      <main className="flex-1 p-3 md:p-6 pb-16 flex justify-center overflow-y-auto">
        <div className="w-full max-w-[1200px] space-y-4 md:space-y-6 pb-16">

          {/* Next Data Update Info */}
          <NextScrapingProgress />

          {competitors.length === 0 && !isLoading ? (
            <div className="glass-card rounded-2xl p-8 md:p-12 border border-border/50">
              <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12">
                {/* Radar */}
                <div className="flex-shrink-0 self-center md:self-start md:mt-1">
                  <LoadingRadar />
                </div>

                {/* Content */}
                <div className="flex-1">
                  <div className="max-w-md space-y-1">
                    <h3 className="text-lg font-semibold">Jeszcze nie masz nikogo na radarze!</h3>
                    <p className="text-muted-foreground text-sm">
                      Wrzuć konkurenta na radar — śledź ich wzrost i porównuj ze swoimi wynikami.
                    </p>
                    
                    {/* Add competitor form */}
                    {renderVerificationInputs()}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Time Range Selector - above tabs */}
              <div className="flex justify-start">
                <div className="glass-card rounded-xl p-1 border border-border/50 inline-flex flex-wrap gap-1">
                  <TabButton
                    isActive={timeRange === 'week'}
                    onClick={() => setTimeRange('week')}
                    variant="gradient"
                  >
                    {t('analytics.lastWeek')}
                  </TabButton>
                  <TabButton
                    isActive={timeRange === 'month'}
                    onClick={() => setTimeRange('month')}
                    variant="gradient"
                  >
                    {t('analytics.lastMonth')}
                  </TabButton>
                </div>
              </div>

              {/* Tabs for better organization */}
              <div className="space-y-6">
                <div className="glass-card rounded-xl p-1 border border-border/50 flex justify-between gap-1">
                  <TabButton
                    isActive={activeTab === 'growth'}
                    onClick={() => setActiveTab('growth')}
                    className="flex items-center gap-2 flex-1 justify-center"
                  >
                    <TrendingUp className="h-4 w-4" />
                    <span>{t('competitors.growth')}</span>
                  </TabButton>
                  <TabButton
                    isActive={activeTab === 'overview'}
                    onClick={() => setActiveTab('overview')}
                    className="flex items-center gap-2 flex-1 justify-center"
                  >
                    <BarChart className="h-4 w-4" />
                    <span>{t('competitors.overview')}</span>
                  </TabButton>
                  <TabButton
                    isActive={activeTab === 'manage'}
                    onClick={() => setActiveTab('manage')}
                    className="flex items-center gap-2 flex-1 justify-center"
                  >
                    <Target className="h-4 w-4" />
                    <span>{t('competitors.manage')}</span>
                  </TabButton>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div className="space-y-6">
                    <CompetitorComparison showAll={true} timeRange={timeRange} />
                  </div>
                )}

                {/* Growth Tab */}
                {activeTab === 'growth' && (
                  <div className="space-y-6">
                    <CompetitorGrowthChart timeRange={timeRange} />
                  </div>
                )}

                {/* Manage Tab */}
                {activeTab === 'manage' && (
                  <div className="space-y-6">
                    <div className="glass-card rounded-2xl overflow-hidden border border-border/50">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Target className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-xl font-bold">
                              {t('competitors.manageCompetitors')}
                            </CardTitle>
                            <CardDescription>
                              {t('competitors.addNew')}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Add New Competitor Input */}
                        <div className="p-4 rounded-lg glass border border-border/50">
                          {renderVerificationInputs()}
                        </div>

                        {/* Competitors List */}
                        <div className="space-y-4">
                          {competitors.map((competitor) => (
                            <div key={competitor.id} className="p-4 rounded-xl glass border border-primary/40 shadow-sm hover:border-primary/60 hover:shadow-md transition-all">
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                  <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-primary/10 flex items-center justify-center">
                                    <span className="text-primary font-semibold text-lg">
                                      {competitor.instagram_username.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  
                                  <div className="min-w-0 flex-1">
                                    <h3 className="font-semibold text-base md:text-lg truncate">@{competitor.instagram_username}</h3>
                                  </div>
                                </div>

                                <div className="flex items-center gap-4 md:gap-6">
                                  {/* Stats - hidden on mobile */}
                                  <div className="hidden md:grid grid-cols-3 gap-6 text-center">
                                    <div>
                                      <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mb-1">
                                        <Users className="h-3 w-3" />
                                        {t('competitors.followers')}
                                      </div>
                                      <div className="font-semibold text-lg tabular-nums">{formatNumber(competitor.followers)}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-muted-foreground mb-1">{t('competitors.following')}</div>
                                      <div className="font-semibold text-lg tabular-nums">{formatNumber(competitor.following)}</div>
                                    </div>
                                    <div>
                                      <div className="text-xs text-muted-foreground mb-1">{t('competitors.posts')}</div>
                                      <div className="font-semibold text-lg tabular-nums">{competitor.posts || 0}</div>
                                    </div>
                                  </div>

                                  {/* Delete Action */}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCompetitorToDelete({ id: competitor.id, username: competitor.instagram_username })}
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Stats for mobile - shown below */}
                              <div className="md:hidden grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border/50">
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground mb-1">{t('competitors.followers')}</div>
                                  <div className="font-semibold tabular-nums">{formatNumber(competitor.followers)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground mb-1">{t('competitors.following')}</div>
                                  <div className="font-semibold tabular-nums">{formatNumber(competitor.following)}</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-xs text-muted-foreground mb-1">{t('competitors.posts')}</div>
                                  <div className="font-semibold tabular-nums">{competitor.posts || 0}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          </div>
        </main>

      <AlertDialog open={!!competitorToDelete} onOpenChange={(open) => !open && setCompetitorToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('competitors.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('competitors.removeDesc', { username: competitorToDelete?.username || '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveCompetitor} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('competitors.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Competitors;